/**
 * Infinite scroll for collection pages.
 *
 * Behavior:
 *  - Watches a sentinel element rendered by snippets/pagination.liquid.
 *  - When the sentinel enters the viewport, fetches the next page's HTML,
 *    extracts product cards, and appends them to the current product grid.
 *  - The pagination bar stays visible the whole time, and clicking a page
 *    number still does a full navigation to ?page=N (handled by plain <a> tags).
 *  - Stops when there is no next page.
 *  - Re-initializes itself when the section is replaced (e.g. after a filter
 *    change), by observing DOM mutations on the section.
 */

(function () {
  'use strict';

  if (typeof window === 'undefined') return;

  var SENTINEL_SELECTOR = '.infinite-scroll-sentinel';
  var GRID_SELECTOR = '#product-grid';
  var CONTAINER_SELECTOR = '[data-infinite-scroll]';
  var ROOT_MARGIN = '600px 0px'; // start loading 600px before sentinel enters viewport

  // Tracks pages currently being fetched so we don't double-fetch.
  var inFlight = new Set();

  function initContainer(container) {
    if (!container || container.dataset.infiniteScrollReady === 'true') return;
    container.dataset.infiniteScrollReady = 'true';
    attachObserver(container);
  }

  function attachObserver(container) {
    var sentinel = container.querySelector(SENTINEL_SELECTOR);
    if (!sentinel) return; // no next page, nothing to do

    // If we previously attached an observer to this sentinel, skip.
    if (sentinel.dataset.observed === 'true') return;
    sentinel.dataset.observed = 'true';

    if (!('IntersectionObserver' in window)) {
      // Old browser fallback: do nothing. Pagination links still work.
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var s = entry.target;
          var nextUrl = s.getAttribute('data-next-url');
          if (!nextUrl || inFlight.has(nextUrl)) return;
          loadNextPage(container, s, nextUrl, observer);
        });
      },
      { rootMargin: ROOT_MARGIN, threshold: 0 }
    );

    observer.observe(sentinel);
  }

  function showLoader(sentinel) {
    var loader = sentinel.querySelector('.infinite-scroll-loader');
    if (loader) loader.hidden = false;
  }

  function hideLoader(sentinel) {
    var loader = sentinel.querySelector('.infinite-scroll-loader');
    if (loader) loader.hidden = true;
  }

  function loadNextPage(container, sentinel, url, observer) {
    inFlight.add(url);
    showLoader(sentinel);

    fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) {
        if (!res.ok) throw new Error('Fetch failed: ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');

        // Find the new page's grid items and its sentinel (for the page after this one).
        var newGrid = doc.querySelector(GRID_SELECTOR);
        var newSentinel = doc.querySelector(SENTINEL_SELECTOR);
        var currentGrid = container.querySelector(GRID_SELECTOR);

        if (!newGrid || !currentGrid) {
          // Nothing we can do; bail gracefully.
          observer.unobserve(sentinel);
          sentinel.remove();
          inFlight.delete(url);
          return;
        }

        // Append each new product li to the current grid.
        var newItems = newGrid.querySelectorAll(':scope > .grid__item');
        var fragment = document.createDocumentFragment();
        newItems.forEach(function (li) {
          fragment.appendChild(li);
        });
        currentGrid.appendChild(fragment);

        // Stop observing the old sentinel.
        observer.unobserve(sentinel);
        inFlight.delete(url);

        if (newSentinel) {
          // Replace the old sentinel with the next page's sentinel
          // (which now points to page N+2). Keep it after the grid so
          // pagination remains at the very bottom visually.
          sentinel.replaceWith(newSentinel);
          // Re-attach observer to the new sentinel.
          attachObserver(container);
        } else {
          // No more pages. Remove the sentinel entirely.
          sentinel.remove();
        }
      })
      .catch(function (err) {
        console.error('[infinite-scroll]', err);
        hideLoader(sentinel);
        inFlight.delete(url);
        // Leave the sentinel in place; user can still click pagination links.
      });
  }

  function initAll() {
    document.querySelectorAll(CONTAINER_SELECTOR).forEach(initContainer);
  }

  // Initial run.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // When Shopify's facet/filter code replaces the section's inner HTML
  // (filter applied, sort changed, etc.), the old sentinel is gone and a
  // fresh one is in the DOM. Re-init when that happens.
  //
  // We observe document.body for childList changes inside any container.
  // This is cheap because we early-out via the data-infinite-scroll-ready
  // flag, and the new sentinel has data-observed unset.
  var mo = new MutationObserver(function (mutations) {
    var needsReinit = false;
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      if (m.type !== 'childList') continue;
      for (var j = 0; j < m.addedNodes.length; j++) {
        var node = m.addedNodes[j];
        if (node.nodeType !== 1) continue;
        if (
          node.matches &&
          (node.matches(CONTAINER_SELECTOR) ||
            node.matches(SENTINEL_SELECTOR) ||
            node.querySelector(SENTINEL_SELECTOR))
        ) {
          needsReinit = true;
          break;
        }
      }
      if (needsReinit) break;
    }
    if (needsReinit) {
      // Re-attach observers to any container whose sentinel is fresh.
      document.querySelectorAll(CONTAINER_SELECTOR).forEach(function (container) {
        // Skip the ready check this time so we re-bind to the new sentinel.
        attachObserver(container);
      });
    }
  });

  mo.observe(document.body, { childList: true, subtree: true });
})();